// CMS auth
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

function headers() {
  return {
    'apikey': SUPABASE_KEY,
    'Authorization': 'Bearer ' + SUPABASE_KEY,
    'Content-Type': 'application/json'
  };
}

async function parseRpc(response, label) {
  const text = await response.text();

  if (!response.ok) {
    throw new Error(
      `${label} failed (${response.status}): ${text || response.statusText}`
    );
  }

  return text ? JSON.parse(text) : null;
}

async function requireOk(response, label) {
  const result = await parseRpc(response, label);

  if (!result?.ok) {
    throw new Error(
      result?.message || `${label} failed.`
    );
  }

  return result;
}

export async function requestCmsCode(email) {
  const enteredEmail = String(email || '').toLowerCase().trim();

  if (!enteredEmail) {
    throw new Error('Enter an email address.');
  }

  const response = await fetch(
  `${SUPABASE_URL}/rest/v1/instructors?select=id,name,email,is_superadmin,password_set&email=eq.${encodeURIComponent(enteredEmail)}&limit=2`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  if (!response.ok) {
    throw new Error(`Could not look up instructor account (${response.status}).`);
  }

  const data = await response.json();

  if (!Array.isArray(data) || data.length === 0) {
    throw new Error('No account found for that email address.');
  }

  if (data.length > 1) {
    throw new Error('More than one instructor account uses that email address.');
  }

  return data[0];
}

export async function loginCmsWithPassword(instructorId, password) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/cms_password_login`,
    {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({
        p_instructor_id: instructorId,
        p_password: password
      })
    }
  );

  const result = await requireOk(
    response,
    'Signing in'
  );

  if (!result?.token || !result?.instructor) {
    throw new Error('Could not create the CMS session.');
  }

  return result;
}

export async function setInitialInstructorPassword(
  instructorId,
  code,
  password
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/set_initial_instructor_password`,
    {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({
        p_instructor_id: instructorId,
        p_code: String(code || '').trim(),
        p_password: password
      })
    }
  );

  return requireOk(
    response,
    'Setting instructor password'
  );
}

export async function resetInstructorPassword(
  instructorId,
  code,
  password
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/reset_instructor_password`,
    {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({
        p_instructor_id: instructorId,
        p_code: String(code || '').trim(),
        p_password: password
      })
    }
  );

  return requireOk(
    response,
    'Resetting instructor password'
  );
}

/*
 * Retained for compatibility with older code/builds.
 */
export async function verifyCmsLogin(instructorId, code) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/cms_verify_login`,
    {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({
        p_instructor_id: instructorId,
        p_code: String(code || '').trim()
      })
    }
  );

  const result = await parseRpc(response, 'Verifying CMS login');

  if (!result?.ok || !result?.token || !result?.instructor) {
    throw new Error(
      result?.status === 'invalid_code'
        ? 'Invalid or expired code.'
        : result?.message || 'Could not create the CMS session.'
    );
  }

  return result;
}

export function saveCmsSession(instructor, cmsToken) {
  if (!instructor?.id || !instructor?.email || !cmsToken) {
    throw new Error('Invalid instructor account.');
  }

  const session = {
    instructorId: instructor.id,
    email: instructor.email,
    name: instructor.name || '',
    isSuperadmin: instructor.is_superadmin === true,
    cmsToken,
    expiresAt: Date.now() + (30 * 24 * 60 * 60 * 1000)
  };

  localStorage.setItem('cmsSession', JSON.stringify(session));
  return session;
}

export function getCmsSession() {
  try {
    const raw = localStorage.getItem('cmsSession');

    if (!raw) {
      return null;
    }

    const session = JSON.parse(raw);

    if (
      !session.instructorId ||
      !session.email ||
      !session.cmsToken ||
      !session.expiresAt ||
      Date.now() > session.expiresAt
    ) {
      localStorage.removeItem('cmsSession');
      return null;
    }

    return {
      ...session,
      isSuperadmin: session.isSuperadmin === true
    };
  } catch (e) {
    localStorage.removeItem('cmsSession');
    return null;
  }
}

export async function revokeCmsSession(cmsToken) {
  const token = String(cmsToken || '').trim();
  if (!token) return;

  try {
    await fetch(
      `${SUPABASE_URL}/rest/v1/rpc/cms_revoke_session`,
      {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ p_token: token })
      }
    );
  } catch (err) {
    console.error('Revoking CMS session failed:', err);
  }
}

export async function generateMasterStudentId(cmsToken) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/cms_generate_master_login_id`,
    {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ p_token: cmsToken })
    }
  );

  const result = await parseRpc(response, 'Generating Master Student ID');

  if (!result?.ok || !result?.code) {
    throw new Error(
      result?.status === 'not_superadmin'
        ? 'Only a superadmin can generate a Master Student ID.'
        : result?.message || 'Could not generate a Master Student ID.'
    );
  }

  return result;
}

export function clearCmsSession() {
  localStorage.removeItem('cmsSession');
}
